import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/16/solid'
import { Button } from '@/components/tailwind/button'
import { Heading } from '@/components/tailwind/heading'
import { Card } from '@/components/ui/card'
import { Text } from '@/components/tailwind/text'
import { useAuth } from '@/context/use-auth'
import { getBillingRunById, getBillingRunItems, type BillingRun, type BillingRunItem } from '@/services/billing-services'
import { BillingRunItemTableEnhanced } from './components/billing-run-item-table-enhanced'
import { formatDateString } from '@/lib/utils'
import { useBillingTranslation } from '@/i18n/hooks'
import { calculateBillingProgress, getProgressText, getProgressColor } from '@/utils/billing-progress'
import log from '@/lib/logger'

function BillingRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { t } = useBillingTranslation()
  const [billingRun, setBillingRun] = useState<BillingRun | null>(null)
  const [billingRunItems, setBillingRunItems] = useState<BillingRunItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id && token) {
      fetchBillingRunDetails()
    }
  }, [id, token])

  async function fetchBillingRunDetails() {
    if (!id || !token) {
      setError(t('errors.missingIdOrToken'))
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Fetch billing run details and items in parallel
      const [runData, itemsData] = await Promise.all([
        getBillingRunById(token, id),
        getBillingRunItems(token, id)
      ])
      
      if (runData) {
        setBillingRun(runData)
        setBillingRunItems(itemsData)
      } else {
        setError(t('page.detail.notFound'))
      }
    } catch (_error) {
      log.error('Error fetching billing run details:', _error)
      setError(t('errors.loadDetail'))
    } finally {
      setLoading(false)
    }
  }

  function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }


  const stats = useMemo(() => {
    const membershipFeeSum = billingRunItems
      .filter(item => item.itemType === 'MEMBERSHIP_FEE')
      .reduce((sum, item) => sum + item.amount, 0)
    const oneTimeDonationSum = billingRunItems
      .filter(item => item.itemType === 'ONE_TIME_DONATION')
      .reduce((sum, item) => sum + item.amount, 0)
    const annualDonationSum = billingRunItems
      .filter(item => item.itemType === 'ANNUAL_DONATION')
      .reduce((sum, item) => sum + item.amount, 0)
    const paidSum = billingRunItems
      .filter(item => item.paymentStatus === 'PAID')
      .reduce((sum, item) => sum + item.amount, 0)
    const pendingSum = billingRunItems
      .filter(item => item.paymentStatus === 'PENDING' || !item.paymentStatus)
      .reduce((sum, item) => sum + item.amount, 0)
    const failedSum = billingRunItems
      .filter(item => item.paymentStatus === 'FAILED')
      .reduce((sum, item) => sum + item.amount, 0)
    const cancelledSum = billingRunItems
      .filter(item => item.paymentStatus === 'CANCELLED')
      .reduce((sum, item) => sum + item.amount, 0)
    return {
      membershipFeeSum,
      oneTimeDonationSum,
      annualDonationSum,
      paidSum,
      pendingSum,
      failedSum,
      cancelledSum
    }
  }, [billingRunItems])

  function getProgressBar(progress: number) {
    const progressColor = getProgressColor(progress)
    const progressText = getProgressText(progress, t)
    
    return (
      <div className="w-full max-w-sm">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600 dark:text-gray-400">{progressText}</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className={`${progressColor} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    )
  }

  function handleBackClick() {
    navigate('/finance/billing')
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6 max-w-7xl space-y-6">
        <div className="flex items-center gap-3">
          <Button plain onClick={handleBackClick}>
            <ArrowLeftIcon className="h-4 w-4" />
{t('actions.back')}
          </Button>
          <Heading>{t('page.detail.title')}</Heading>
        </div>
        <div className="flex justify-center items-center h-64">
          <Text className="text-gray-500">{t('page.detail.loading')}</Text>
        </div>
      </div>
    )
  }

  if (error || !billingRun) {
    return (
      <div className="container mx-auto py-6 max-w-7xl space-y-6">
        <div className="flex items-center gap-3">
          <Button plain onClick={handleBackClick}>
            <ArrowLeftIcon className="h-4 w-4" />
{t('actions.back')}
          </Button>
          <Heading>{t('page.detail.title')}</Heading>
        </div>
        <Card className="p-6">
          <div className="flex justify-center items-center h-64">
            <Text className="text-red-500">
              {error || t('page.detail.notFound')}
            </Text>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 max-w-7xl space-y-6" data-testid="billing-run-details">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button plain onClick={handleBackClick}>
            <ArrowLeftIcon className="h-4 w-4" />
{t('actions.back')}
          </Button>
          <div>
            <Heading>{t('page.detail.title')}</Heading>
            <Text className="text-gray-500">ID: {billingRun.id}</Text>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getProgressBar(calculateBillingProgress(billingRunItems))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <Text className="text-sm text-gray-500">{t('summary.totalAmount')}</Text>
          <Text className="text-xl font-semibold mt-1">
            {formatAmount(billingRun.totalAmount)}
          </Text>
        </Card>

        <Card className="p-6">
          <Text className="text-sm text-gray-500">{t('summary.entries')}</Text>
          <Text className="text-xl font-semibold mt-1">
            {billingRun.membershipFeeCount + billingRun.oneTimeDonationCount + billingRun.annualDonationCount}
          </Text>
          <div className="mt-2 space-y-1 text-sm text-gray-500">
            <div className="flex justify-between">
              <span>{t('summary.membershipFees')}</span>
              <span>{billingRun.membershipFeeCount}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('summary.oneTimeDonations')}</span>
              <span>{billingRun.oneTimeDonationCount}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('summary.annualDonations')}</span>
              <span>{billingRun.annualDonationCount}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <Text className="text-sm text-gray-500">{t('summary.createdAt')}</Text>
          <Text className="text-xl font-semibold mt-1">
            {formatDateString(billingRun.createdAt)}
          </Text>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <Text className="text-sm font-medium text-gray-500 mb-3">{t('summary.revenueByType')}</Text>
          <div className="space-y-2">
            <div className="flex justify-between">
              <Text className="text-sm">{t('summary.membershipFees')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.membershipFeeSum)}</Text>
            </div>
            <div className="flex justify-between">
              <Text className="text-sm">{t('summary.oneTimeDonations')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.oneTimeDonationSum)}</Text>
            </div>
            <div className="flex justify-between">
              <Text className="text-sm">{t('summary.annualDonations')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.annualDonationSum)}</Text>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <Text className="text-sm font-medium text-gray-500 mb-3">{t('summary.statusBreakdown')}</Text>
          <div className="space-y-2">
            <div className="flex justify-between">
              <Text className="text-sm text-green-600 dark:text-green-400">{t('summary.paidAmount')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.paidSum)}</Text>
            </div>
            <div className="flex justify-between">
              <Text className="text-sm text-amber-600 dark:text-amber-400">{t('summary.pendingAmount')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.pendingSum)}</Text>
            </div>
            <div className="flex justify-between">
              <Text className="text-sm text-red-600 dark:text-red-400">{t('summary.failedAmount')}</Text>
              <Text className="text-sm font-medium">{formatAmount(stats.failedSum)}</Text>
            </div>
            {stats.cancelledSum > 0 && (
              <div className="flex justify-between">
                <Text className="text-sm text-gray-500 dark:text-gray-400">{t('summary.cancelledAmount')}</Text>
                <Text className="text-sm font-medium line-through">{formatAmount(stats.cancelledSum)}</Text>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="mb-4">
          <Heading level={2} className="text-base font-semibold">
{t('summary.itemsTitle')}
          </Heading>
          <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
{t('summary.itemsDescription')}
          </Text>
        </div>
        
        <BillingRunItemTableEnhanced 
          items={billingRunItems} 
          loading={false}
          onItemsUpdated={setBillingRunItems}
          billingRunDate={billingRun.createdAt}
          billingRunId={billingRun.id}
          sepaXmlGeneratedAt={billingRun.sepaXmlGeneratedAt}
          onSepaXmlDownloaded={fetchBillingRunDetails}
        />
      </Card>
    </div>
  )
}

export default BillingRunDetailPage